'use client'

// The library's refusal states, in one place so the /library page and the
// in-editor panel say the same thing. Each is a legitimate server answer, not
// an error — the point of splitting them is that the recovery differs: a guest
// signs up, a former member switches workspace.

import { useGatedFeature } from '@/components/auth'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { LibraryAccessDenialReason } from '@/store/libraryTransport'

/** `className` carries the host's own fill rule — the page centers inside a
    flex column, the editor rail inside a plain scroll block. */
export function LibraryAccessDeniedNotice({
  reason,
  className,
}: {
  reason: LibraryAccessDenialReason | null
  className?: string
}) {
  const { showSignUp, SignUpModalComponent } = useGatedFeature()
  const shell = cn('flex flex-col items-center justify-center gap-1.5 px-6 text-center', className)

  if (reason === 'requires-account') {
    return (
      <div className={shell}>
        <p className="text-xs text-muted-foreground">A library needs an account.</p>
        <p className="text-[10px] text-muted-foreground/60">
          Sign up to keep collections, uploads, and variables across projects.
        </p>
        <Button size="sm" className="mt-2" onClick={() => showSignUp('library-access-denied')}>
          Sign up
        </Button>
        {SignUpModalComponent}
      </div>
    )
  }

  if (reason === 'account-unavailable') {
    return (
      <div className={shell}>
        <p className="text-xs text-muted-foreground">This account can’t open a library.</p>
        <p className="text-[10px] text-muted-foreground/60">
          Sign in with another account, or contact support@martini.film.
        </p>
      </div>
    )
  }

  return (
    <div className={shell}>
      <p className="text-xs text-muted-foreground">You don’t have access to this workspace.</p>
      <p className="text-[10px] text-muted-foreground/60">Switch workspaces to continue.</p>
    </div>
  )
}
